import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

declare const cornerstone: any;
declare const cornerstoneMath: any;
declare const cornerstoneTools: any;
declare const dicomParser: any;
declare const Hammer: any;
declare global {
  interface Window {
    cornerstoneWADOImageLoader: any;
  }
}

@Component({
  selector: 'app-dicom-viewer',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './dicom-viewer.component.html',
  styleUrl: './dicom-viewer.component.scss',
})
export class DicomViewerComponent implements AfterViewInit, OnDestroy {
  @Input() src!: string;
  @ViewChild('container', { static: true })
  container!: ElementRef<HTMLDivElement>;

  activeTool = 'Wwwc';
  imageInfo: any = null;
  toolsInitialized = false;
  private wadoLoaderLoaded = false;

  constructor(private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.loadWadoImageLoader().then(() => {
      this.initCornerstone();
      this.loadDicomImage();
      this.setupKeyboardShortcuts();
    });
  }

  private loadWadoImageLoader(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.cornerstoneWADOImageLoader) {
        this.wadoLoaderLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'assets/js/cornerstoneWADOImageLoader.bundle.min.js';
      script.onload = () => {
        this.wadoLoaderLoaded = true;
        resolve();
      };
      script.onerror = (error) => {
        console.error('Failed to load cornerstoneWADOImageLoader', error);
        reject(error);
      };
      document.head.appendChild(script);
    });
  }

  private initCornerstone() {
    const element = this.container.nativeElement;

    const wadoLoader = window.cornerstoneWADOImageLoader;
    wadoLoader.external.cornerstone = cornerstone;
    wadoLoader.external.dicomParser = dicomParser;

    cornerstoneTools.external.cornerstone = cornerstone;
    cornerstoneTools.external.cornerstoneMath = cornerstoneMath;
    cornerstoneTools.external.Hammer = Hammer;

    if (!this.toolsInitialized) {
      cornerstoneTools.init({
        mouseEnabled: true,
        touchEnabled: true,
        globalToolSyncEnabled: false,
        showSVGCursors: true,
      });

      cornerstoneTools.addTool(cornerstoneTools.WwwcTool);
      cornerstoneTools.addTool(cornerstoneTools.ZoomTool);
      cornerstoneTools.addTool(cornerstoneTools.PanTool);
      cornerstoneTools.addTool(cornerstoneTools.LengthTool);
      cornerstoneTools.addTool(cornerstoneTools.AngleTool);
      cornerstoneTools.addTool(cornerstoneTools.EllipticalRoiTool);
      cornerstoneTools.addTool(cornerstoneTools.RectangleRoiTool);

      const brandPrimary = getComputedStyle(document.documentElement)
        .getPropertyValue('--brand-primary')
        .trim() || '#009448';
      cornerstoneTools.toolColors.setToolColor(brandPrimary);
      cornerstoneTools.toolColors.setActiveColor(brandPrimary);

      this.toolsInitialized = true;
    }

    cornerstone.enable(element);
  }

  private loadDicomImage() {
    const element = this.container.nativeElement;

    this.zone.runOutsideAngular(() => {
      cornerstone.loadImage(this.src).then((image: any) => {
        cornerstone.displayImage(element, image);

        this.extractImageInfo(image);

        cornerstoneTools.addStackStateManager(element, ['stack']);
        cornerstoneTools.clearToolState(element, 'stack');
        cornerstoneTools.addToolState(element, 'stack', {
          currentImageIdIndex: 0,
          imageIds: [this.src],
        });

        const tools = [
          { name: 'Wwwc', tool: cornerstoneTools.WwwcTool },
          { name: 'Zoom', tool: cornerstoneTools.ZoomTool },
          { name: 'Pan', tool: cornerstoneTools.PanTool },
          { name: 'Length', tool: cornerstoneTools.LengthTool },
          { name: 'Angle', tool: cornerstoneTools.AngleTool },
          { name: 'EllipticalRoi', tool: cornerstoneTools.EllipticalRoiTool },
          { name: 'RectangleRoi', tool: cornerstoneTools.RectangleRoiTool },
        ];

        tools.forEach((t) => {
          try {
            cornerstoneTools.addToolForElement(element, t.tool);
          } catch (e) {}
        });

        this.setActiveTool('Wwwc');

        cornerstone.updateImage(element);
      });
    });
  }

  private extractImageInfo(image: any) {
    try {
      const dataSet = image.data;
      if (dataSet) {
        this.imageInfo = {
          patientName: dataSet.string('x00100010') || 'N/A',
          studyDate: dataSet.string('x00080020') || 'N/A',
          modality: dataSet.string('x00080060') || 'N/A',
        };
      }
    } catch (e) {
      console.warn('Could not extract DICOM metadata');
    }
  }

  setActiveTool(toolName: string) {
    const element = this.container.nativeElement;
    this.activeTool = toolName;

    [
      'Wwwc',
      'Zoom',
      'Pan',
      'Length',
      'Angle',
      'EllipticalRoi',
      'RectangleRoi',
    ].forEach((tool) => {
      try {
        cornerstoneTools.setToolPassiveForElement(element, tool);
      } catch (e) {}
    });

    const mouseButton =
      toolName === 'Wwwc' || toolName === 'Zoom' || toolName === 'Pan' ? 1 : 1;

    cornerstoneTools.setToolActiveForElement(element, toolName, {
      mouseButtonMask: mouseButton,
    });

    cornerstone.updateImage(element);
  }

  resetView() {
    const element = this.container.nativeElement;
    const viewport = cornerstone.getViewport(element);

    cornerstone.reset(element);
    cornerstone.updateImage(element);
  }

  invertImage() {
    const element = this.container.nativeElement;
    const viewport = cornerstone.getViewport(element);

    viewport.invert = !viewport.invert;
    cornerstone.setViewport(element, viewport);
  }

  clearMeasurements() {
    const element = this.container.nativeElement;

    const toolNames = [
      'Length',
      'Angle',
      'EllipticalRoi',
      'RectangleRoi',
      'ArrowAnnotate',
    ];

    toolNames.forEach((toolName) => {
      try {
        cornerstoneTools.clearToolState(element, toolName);
      } catch (e) {}
    });

    cornerstone.updateImage(element);
  }

  private setupKeyboardShortcuts() {
    const handleKeyPress = (e: KeyboardEvent) => {
      switch (e.key.toLowerCase()) {
        case 'w':
          this.setActiveTool('Wwwc');
          break;
        case 'z':
          this.setActiveTool('Zoom');
          break;
        case 'p':
          this.setActiveTool('Pan');
          break;
        case 'l':
          this.setActiveTool('Length');
          break;
        case 'a':
          this.setActiveTool('Angle');
          break;
        case 'r':
          this.resetView();
          break;
        case 'i':
          this.invertImage();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    this.keyboardCleanup = () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }

  private keyboardCleanup?: () => void;

  ngOnDestroy() {
    if (this.keyboardCleanup) {
      this.keyboardCleanup();
    }

    if (this.container) {
      const element = this.container.nativeElement;
      cornerstoneTools.clearToolState(element, 'stack');
      cornerstone.disable(element);
    }
  }
}
